import React from 'react';
import { X } from 'lucide-react';
import {
  Booking,
  BookingBill,
  BookingContainer,
  getMovementDescription,
  Movement,
  MovementType,
} from '../types';

interface ContainerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    container: BookingContainer;
    booking: Booking;
    bill: BookingBill;
    movements: Movement[];
  } | null;
}

// Stiluri inline cu paleta claude_design (CSS variables din claude-design.css).
const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1100,
  background: 'rgba(20, 20, 20, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  backdropFilter: 'blur(2px)',
};

const dialogStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-lg, 12px)',
  boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
  width: '100%',
  maxWidth: 760,
  maxHeight: '90vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  color: 'var(--text)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '16px 20px',
  borderBottom: '1px solid var(--border)',
};

const closeBtnStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-muted)',
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
  flexShrink: 0,
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: 20,
  background: 'var(--surface-2)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const infoCardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-md, 8px)',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  margin: 0,
};

const dlRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 10,
  fontSize: 13,
  alignItems: 'baseline',
};

export const ContainerDetailsModal: React.FC<ContainerDetailsModalProps> = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null;

  const { container, booking, bill, movements } = data;

  return (
    <div
      style={backdropStyle}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={dialogStyle}>
        <div style={headerStyle}>
          <div style={{ minWidth: 0 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'var(--text)',
              }}
            >
              Detalii container{' '}
              <span
                className="mono"
                style={{
                  color: 'var(--accent)',
                  background: 'var(--accent-soft)',
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: 14,
                  marginLeft: 4,
                }}
              >
                {container.containerNumber}
              </span>
            </h3>
          </div>
          <button onClick={onClose} aria-label="Închide" style={closeBtnStyle}>
            <X size={16} />
          </button>
        </div>

        <div style={bodyStyle}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 14,
            }}
          >
            <div style={infoCardStyle}>
              <h4 style={sectionTitleStyle}>Informații Booking</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <DlRow label="Referință" value={booking.bookingReference} mono />
                <DlRow label="Client" value={booking.clientName} />
                <DlRow label="Număr Booking" value={booking.bookingReference} mono />
                <DlRow label="Număr BL" value={bill.billNumber} mono />
              </div>
            </div>

            <div style={infoCardStyle}>
              <h4 style={sectionTitleStyle}>Specificații</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <DlRow label="Tip" value={`${container.size}${container.type}`} />
                <DlRow label="Sigiliu" value={container.sealNumber || '—'} mono />
                <DlRow label="Greutate" value={container.grossWeight ? `${container.grossWeight} kg` : '—'} />
                <DlRow label="Nr. colete" value={container.packageNumber ? String(container.packageNumber) : '—'} />
                <DlRow label="Marfă" value={bill.commodity || '—'} truncate />
              </div>
            </div>
          </div>

          <div style={infoCardStyle}>
            <h4 style={{ ...sectionTitleStyle, marginBottom: 8 }}>Istoric Container</h4>
            {movements.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '8px 0' }}>
                Nu există mișcări înregistrate.
              </div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {movements.map((m, idx) => (
                  <TimelineRow key={idx} movement={m} isLast={idx === movements.length - 1} />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DlRow: React.FC<{ label: string; value: string; mono?: boolean; truncate?: boolean }> = ({
  label,
  value,
  mono,
  truncate,
}) => (
  <div style={dlRowStyle}>
    <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
    <span
      className={mono ? 'mono' : undefined}
      style={{
        color: 'var(--text)',
        fontWeight: 500,
        textAlign: 'right',
        ...(truncate
          ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }
          : {}),
      }}
    >
      {value}
    </span>
  </div>
);

const TimelineRow: React.FC<{ movement: Movement; isLast: boolean }> = ({ movement, isLast }) => {
  const type = movement.movement as MovementType;
  const isOcean = type === MovementType.OF || type === MovementType.OE;
  const dotBg = isOcean ? 'var(--accent)' : 'var(--text)';
  const dotFg = isOcean ? 'var(--accent-fg)' : 'var(--surface)';
  const date = movement.date
    ? new Date(movement.date).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  return (
    <li style={{ position: 'relative', paddingLeft: 40, paddingBottom: isLast ? 0 : 18 }}>
      {!isLast && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 28,
            left: 13,
            bottom: 0,
            width: 2,
            background: 'var(--border)',
          }}
        />
      )}
      <span
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 28,
          height: 28,
          borderRadius: 999,
          background: dotBg,
          color: dotFg,
          display: 'grid',
          placeItems: 'center',
          fontSize: 10,
          fontWeight: 700,
          border: '2px solid var(--surface)',
          boxShadow: '0 0 0 1px var(--border)',
        }}
      >
        {movement.movement}
      </span>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
            {movement.loadingPort || '—'}
            {movement.dischargePort ? ` → ${movement.dischargePort}` : ''}
            {movement.vesselName && (
              <span style={{ marginLeft: 8, color: 'var(--accent)', fontWeight: 500 }}>
                via {movement.vesselName}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {getMovementDescription(type)}
          </div>
        </div>
        <span
          style={{
            fontSize: 12,
            color: 'var(--text-muted)',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {date}
        </span>
      </div>
    </li>
  );
};
